import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../sporttia/AppContext';
import { formatPrice } from '../../lib/utils';
import Cashdro, { OP_ERROR } from '../../lib/Cashdro';
import constants from '../../config/constants';

type CashdroControlDialogProps = {
    open: boolean;
    amount: number;
    onClose: () => void;
    onSuccess: () => void;
};

type CashdroQueryResult = {
    operation: any;
    devices: any[];
};

type CashdroApiResult = {
    code: number;
    data?: string;
    error?: {
        msg: string;
    };
};

export default function CashdroControlDialog({
    open,
    amount,
    onClose,
    onSuccess,
}: CashdroControlDialogProps): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [operationId, setOperationId] = useState<string | null>(null);
    const [operationOngoing, setOperationOngoing] = useState(false);
    const [noChange, setnoChange] = useState(false);
    const [error, setError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [cashdroService, setCashdroService] = useState<Cashdro | null>(null);

    useEffect(() => {
        if (open) {
            try {
                const service = new Cashdro();
                setCashdroService(service);
            } catch (err: unknown) {
                const errorMessage =
                    err instanceof Error ? err.message : String(err);
                setError(errorMessage);
                setDisabled(true);
            }
        }
        // cleanup
        return () => {
            setCashdroService(null);
        };
    }, [open]);

    const onCancel = async (): Promise<void> => {
        if (cashdroService) {
            const result = await cashdroService
                .cancelOperation(operationId!)
                .catch((err: unknown) => {
                    const errorMessage =
                        (err as { error?: { msg?: string } })?.error?.msg ||
                        String(err);
                    cxt?.showMessage('E', errorMessage);
                });

            if (result && result.code === 1) {
                cxt?.showMessage('E', 'Cancelado!');
            }
        }
        onClose();
    };

    const onAccept = async (): Promise<void> => {
        setOperationOngoing(true);

        let result: CashdroApiResult | undefined = await cashdroService!
            .startOperation(
                constants.cashdro.op.SALE,
                {
                    amount: amount * 100,
                } /* El cashdro representa las perras en plan 550 = 5,50 € */,
            )
            .catch((err: unknown) => {
                setOperationOngoing(false);
                const errorMessage =
                    (err as { error?: { msg?: string } })?.error?.msg ||
                    String(err);
                setError(errorMessage);
                return undefined;
            });

        if (result && result.code === 1) {
            const opId = result.data;

            if (opId) {
                setOperationId(opId);

                result = await cashdroService!
                    .confirmOperation(opId)
                    .catch((err: unknown) => {
                        const errorMessage =
                            (err as { error?: { msg?: string } })?.error?.msg ||
                            String(err);
                        setError(errorMessage);
                        setOperationOngoing(false);
                        return undefined;
                    });

                if (result && result.code === 1) {
                    /* trozo cortado */

                    await cashdroService!.whileQueryingOperation(
                        {
                            operationId: opId,
                        },
                        async ({ operation, devices }: CashdroQueryResult) => {
                            console.log(`Operación ${opId} finalizada`);

                            console.log(operation);

                            // Evaluamos si se introdujo el dinero y cuadra el dinero introducido + el devuelto
                            const { success, error: operationError } =
                                Cashdro.saleOperationSuccessful(operation);

                            // Ocurrió algún error conocido
                            if (!success) {
                                switch (operationError) {
                                    case OP_ERROR.CANCELLED:
                                        console.log('OP_ERROR.CANCELLED');
                                        await onCancel();
                                        break;
                                    case OP_ERROR.HARDWARE:
                                        console.log('OP_ERROR.HARDWARE');
                                        setOperationOngoing(false);
                                        break;
                                    case OP_ERROR.NO_CHANGE:
                                        console.log('OP_ERROR.NO_CHANGE');
                                        setnoChange(true);

                                        result = await cashdroService!
                                            .startOperation(
                                                constants.cashdro.op.REFUND,
                                            )
                                            .catch((err: unknown) => {
                                                const errorMessage =
                                                    (
                                                        err as {
                                                            error?: {
                                                                msg?: string;
                                                            };
                                                        }
                                                    )?.error?.msg ||
                                                    String(err);
                                                setError(errorMessage);
                                                setOperationOngoing(false);
                                                console.log(
                                                    'Error al iniciar op de reembolso',
                                                );
                                                return undefined;
                                            });

                                        if (result && result.code === 1) {
                                            const refundOpId = result.data!;
                                            result = await cashdroService!
                                                .confirmOperation(refundOpId)
                                                .catch((err: unknown) => {
                                                    const errorMessage =
                                                        (
                                                            err as {
                                                                error?: {
                                                                    msg?: string;
                                                                };
                                                            }
                                                        )?.error?.msg ||
                                                        String(err);
                                                    setError(errorMessage);
                                                    setOperationOngoing(false);
                                                    console.log(
                                                        'Error al confirmar op. de reembolso',
                                                    );
                                                    return undefined;
                                                });

                                            if (result && result.code === 1) {
                                                await cashdroService!.whileQueryingOperation(
                                                    {
                                                        operationId: refundOpId,
                                                        updateOnlyOnce: true,
                                                    },
                                                    async () => {
                                                        console.log(
                                                            'Op de reembolso "completada"',
                                                        );

                                                        await cashdroService!
                                                            .importOperation(
                                                                refundOpId,
                                                            )
                                                            .catch(
                                                                (
                                                                    err: unknown,
                                                                ) => {
                                                                    const errorMessage =
                                                                        (
                                                                            err as {
                                                                                error?: {
                                                                                    msg?: string;
                                                                                };
                                                                            }
                                                                        )?.error
                                                                            ?.msg ||
                                                                        String(
                                                                            err,
                                                                        );
                                                                    cxt?.showMessage(
                                                                        'E',
                                                                        errorMessage,
                                                                    );
                                                                },
                                                            );

                                                        setnoChange(false);

                                                        // Cancelamos la op. inicial
                                                        await onCancel();
                                                    },
                                                    (err: unknown) => {
                                                        console.log(
                                                            'Err al consultar reembolso: ',
                                                            err,
                                                        );
                                                    },
                                                    async () => {
                                                        const availablePieces =
                                                            {
                                                                monedas:
                                                                    devices?.find(
                                                                        (
                                                                            device,
                                                                        ) =>
                                                                            device.type ===
                                                                            '2',
                                                                    )?.pieces ||
                                                                    [],
                                                                billetes:
                                                                    devices?.find(
                                                                        (
                                                                            device,
                                                                        ) =>
                                                                            device.type ===
                                                                            '3',
                                                                    )?.pieces ||
                                                                    [],
                                                            };

                                                        // Cargamos la disponibilidad de piezas (monedas y billetes) devuelta por la operación de pago
                                                        cashdroService!.setAvailablePieces(
                                                            availablePieces,
                                                        );

                                                        // Calculamos las piezas necesarias para efectuar la devolución
                                                        const refundPieces =
                                                            cashdroService!.calculateNecessaryPieces(
                                                                operation.totalin,
                                                            );

                                                        // Comprobamos si las piezas generadas son válidas (por si acaso).
                                                        if (
                                                            Cashdro.validRefundPieces(
                                                                refundPieces,
                                                                operation.totalin,
                                                            )
                                                        ) {
                                                            // Finalizar operación de devolución
                                                            await cashdroService!
                                                                .confirmRefund(
                                                                    refundOpId,
                                                                    refundPieces,
                                                                )
                                                                .catch(
                                                                    (
                                                                        err: unknown,
                                                                    ) => {
                                                                        console.log(
                                                                            err,
                                                                        );
                                                                        const errorMessage =
                                                                            (
                                                                                err as {
                                                                                    error?: {
                                                                                        msg?: string;
                                                                                    };
                                                                                }
                                                                            )
                                                                                ?.error
                                                                                ?.msg ||
                                                                            String(
                                                                                err,
                                                                            );
                                                                        cxt?.showMessage(
                                                                            'E',
                                                                            errorMessage,
                                                                        );
                                                                    },
                                                                );
                                                        } else {
                                                            // Finalizar op. pero con cancelación
                                                            await cashdroService!.finishOperation(
                                                                refundOpId,
                                                                {},
                                                                constants
                                                                    .cashdro.op
                                                                    .CANCEL_WITHDRAWAL,
                                                            );

                                                            setError(
                                                                'Ocurrió un error con la devolución. Verifique el dispositivo CashDro',
                                                            );
                                                            setnoChange(false);
                                                            await onCancel();
                                                        }
                                                    },
                                                );
                                            }
                                        }
                                        break;
                                    case OP_ERROR.UNAVAILABLE:
                                    default:
                                        console.log('OP_ERROR.UNAVAILABLE');
                                        setError(OP_ERROR.UNAVAILABLE);
                                        setOperationOngoing(false);
                                        break;
                                }

                                // op iniciada

                                // op confirmada
                            } else {
                                await cashdroService!
                                    .importOperation(opId)
                                    .catch((err: unknown) => {
                                        const errorMessage =
                                            (
                                                err as {
                                                    error?: { msg?: string };
                                                }
                                            )?.error?.msg || String(err);
                                        cxt?.showMessage('E', errorMessage);
                                    });

                                // Éxito en la op. de venta
                                onSuccess();
                            }

                            setOperationOngoing(false);
                        },
                        (err: unknown) => {
                            console.log(err);
                            setError(String(err));
                        },
                    );
                } else {
                    cxt?.showMessage('E', `Error: ${result?.data}`);
                }
            } else {
                cxt?.showMessage(
                    'E',
                    'Error: no se pudo recuperar "operationID"',
                );
            }
        }
    };

    /**
     * Decidir qué mensaje pintamos en el diálogo
     * @returns {React.ReactElement}
     */
    const renderMessage = (): React.ReactElement => {
        let result: React.ReactElement;
        if (noChange) {
            result = (
                <Box style={{ color: '#e51919' }}>{t('cashdro.noChange')}</Box>
            );
        } else if (operationOngoing) {
            result = <Box>{t('cashdro.waitingForDevice')}</Box>;
        } else if (error) {
            result = <Box style={{ color: '#e51919' }}>{error}</Box>;
        } else {
            result = (
                <Box>
                    {t('cashdro.paymentOrder', {
                        amount: formatPrice(amount, '€'),
                    })}
                </Box>
            );
        }

        return result;
    };

    /**
     * Decidir qué botones pintamos en el diálogo
     * @returns {*[]}
     */
    const renderActions = (): React.ReactElement[] => {
        const actions: React.ReactElement[] = [];

        if (!disabled) {
            if (operationOngoing || error) {
                actions.push(
                    <Button
                        key="abort"
                        onClick={() => {
                            onCancel().catch((err: any) => {
                                console.error('Error canceling:', err);
                            });
                        }}
                        color="primary"
                        variant="contained"
                        className="button-delete"
                    >
                        Abortar operación
                    </Button>,
                );
            } else if (!noChange) {
                actions.push(
                    <Button
                        onClick={() => {
                            onAccept().catch((err: any) => {
                                console.error('Error accepting:', err);
                            });
                        }}
                        key="accept"
                        variant="contained"
                        color="primary"
                    >
                        {t('Accept')}
                    </Button>,
                );
            }
        }

        actions.push(
            <Button
                onClick={() => {
                    onCancel().catch((err: any) => {
                        console.error('Error canceling:', err);
                    });
                }}
                key="cancel"
                variant="contained"
                color="inherit"
            >
                {t('Cancel')}
            </Button>,
        );

        return actions;
    };

    const title = 'Pago mediante CashDro';

    return (
        <Dialog open={open} onClose={onClose}>
            {title && <DialogTitle>{title}</DialogTitle>}
            <DialogContent>
                {renderMessage()}
                {(operationOngoing || noChange) && (
                    <Box display="flex" justifyContent="center" mt={2} mb={2}>
                        <CircularProgress />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>{renderActions()}</DialogActions>
        </Dialog>
    );
}
